import { api, unwrap } from '../api/client';
import { AppError } from '../api/errors';
import type { components } from '../api/schema';

export type DocumentMeta = Omit<components['schemas']['DocumentCreate'], 'name' | 'mime' | 'size'>;
export type UploadStep = 'metadata' | 'upload' | 'verify';

/** SHA-256 hex al fișierului, calculat în browser (`crypto.subtle`), trimis la `complete` ca verificare suplimentară. */
export async function sha256Hex(file: Blob): Promise<string | null> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/**
 * Fluxul D8: `POST /documents` (metadate → URL PUT presemnat) → `PUT` direct în stocare → `POST /documents/{id}/complete`
 * (serverul verifică dimensiunea, tipul după conținut și hash-ul). Întoarce documentul disponibil.
 */
export async function uploadDocument(
  file: File,
  meta: DocumentMeta,
  onStep?: (step: UploadStep) => void,
): Promise<components['schemas']['Document']> {
  onStep?.('metadata');
  const ticket = await unwrap(
    api.POST('/documents', {
      body: {
        ...meta,
        name: file.name,
        mime: file.type as components['schemas']['DocumentMime'],
        size: file.size,
      },
    }),
  );
  onStep?.('upload');
  let put: Response;
  try {
    put = await fetch(ticket.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: ticket.uploadHeaders,
    });
  } catch (e) {
    throw AppError.network(e);
  }
  if (!put.ok) {
    throw new AppError(
      put.status,
      'storage-upload-failed',
      'Încărcarea în stocare a eșuat',
      'Stocarea a refuzat fișierul. Verifică conexiunea și încearcă din nou.',
    );
  }
  onStep?.('verify');
  const sha256 = await sha256Hex(file);
  return unwrap(
    api.POST('/documents/{id}/complete', {
      params: { path: { id: ticket.document.id } },
      body: sha256 ? { sha256 } : {},
    }),
  );
}
